import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Empty, ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Explainer } from '../../components/Explainer'
import { ApiError, api, type DeviceDetailResponse } from '../../lib/api'
import {
  activePoliciesAbsence,
  describeWithholding,
  deviceState,
  envelopeSchema,
  hasCheckedIn
} from './deviceHealth'

/**
 * Everything the server already knew about one device, on a screen.
 *
 * The endpoint behind this page has been implemented, routed and tenant-scoped since Phase 6, and had
 * no caller: no route reached a device, the rows on the list were not links, and so check-in history,
 * the reported envelope schema, the policies a device says it is running, its credentials, its network
 * adapters and its last refused envelope were all unreachable from the product. The console review
 * found the Devices grid unable to tell a healthy machine from one that had never checked in, and the
 * answer was one HTTP call away with nothing calling it.
 *
 * The page is built around absences rather than values, because that is where the fleet screens went
 * wrong: a device that has never checked in is not a device with no policies, and a delivery that
 * cannot be determined is not a delivery with nothing withheld (D-263, D-270).
 */
export function DeviceDetailPage() {
  const { deviceId = '' } = useParams()

  const detail = useAsync(async () => {
    try {
      return await api.getDevice(deviceId)
    } catch (error) {
      // The endpoint answers a bare 404 with no body, which the shared client turns into "The request
      // failed with status 404." — a machine sentence on an operator's screen.
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }, [deviceId])

  if (detail.error) return <ErrorBanner message={detail.error} />
  if (detail.value === null && !detail.loading) {
    return (
      <Empty>
        <span data-testid="device-not-found">
          No device with that identifier is enrolled in this organization.
        </span>
      </Empty>
    )
  }
  if (!detail.value) return <Empty>Loading…</Empty>

  return <DeviceDetail response={detail.value} onChanged={detail.reload} />
}

function DeviceDetail({ response, onChanged }: { response: DeviceDetailResponse; onChanged: () => void }) {
  const device = response.device
  const schema = envelopeSchema(device)
  const neverCheckedIn = !hasCheckedIn(device)
  const policiesAbsence = activePoliciesAbsence(device)
  const state = deviceState(device)

  return (
    <>
      <div className="page-header">
        <div>
          <h1 data-testid="device-detail-name">{device.deviceName}</h1>
          <p>
            {device.hostname && device.hostname !== device.deviceName ? `${device.hostname} · ` : ''}
            {device.operatingSystem} {device.osVersion ?? ''} · agent {device.clientVersion}
          </p>
        </div>
        <div className="toolbar">
          {/*
            The badge answers whether this machine can be reached, not whether the row says `active`.
            This header used to paint a green `active` pill directly above a warning reading "this
            device has never checked in - anything published to it is waiting", which is the same
            contradiction the fleet grid printed nine hundred and sixty-seven times (D-431).
          */}
          <span className={state.pill} data-testid="device-status">
            {state.label}
          </span>
          <RetireButton device={device} onRetired={onChanged} />
          <Link className="button" to="/devices">
            Back to devices
          </Link>
        </div>
      </div>

      <div className="editor-grid">
        <div className="stack">
          <div className="card" data-testid="device-check-in">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Check-in</h3>

              {neverCheckedIn ? (
                // Stated in words rather than left as an empty date. This is the fact the Devices grid
                // could not express: the device has published telemetry, which is what makes it look
                // healthy, and has never polled the management plane at all.
                <div className="banner warning" data-testid="device-never-checked-in">
                  This device has never checked in. It has never collected a policy, never pinned this
                  organization's signing key, and never reported which envelope format it can read.
                  Anything published to it is waiting.
                </div>
              ) : null}

              {/* A reimage and a takeover are the same request here: a valid enrollment token, an
                  install id that matches this row, and a brand-new key, because the old one lived in
                  the data directory a reimage wipes. Blocking it would strand a rebuilt machine for
                  up to a credential period with nothing on screen to say why, so it is shown to
                  somebody who knows whether this machine was rebuilt. */}
              {device.displacedCredentialUtc ? (
                <div className="banner warning" data-testid="device-displaced-credential">
                  This device was re-enrolled on {formatUtc(device.displacedCredentialUtc)} while its
                  previous credential still worked, and that credential was revoked. If this machine
                  was rebuilt or reset around then, that is exactly what it looks like and there is
                  nothing to do. If it was not, somebody enrolled a different machine against this
                  device's identity: revoke the enrollment tokens on the Enrollment tab, and retire this
                  device with the button above.
                </div>
              ) : null}

              <dl className="detail">
                <dt>Last check-in</dt>
                <dd data-testid="device-last-check-in">
                  {neverCheckedIn ? <span className="muted">never</span> : formatUtc(device.lastCheckInUtc)}
                </dd>

                <dt>Check-ins</dt>
                <dd data-testid="device-check-in-count">{device.checkInCount}</dd>

                <dt>Envelope format</dt>
                <dd data-testid="device-envelope-schema">
                  {schema.reported ? schema.label : <span className="muted">{schema.label}</span>}
                </dd>

                {/* What the agent said it can run. Absent from agents before 1.2.0, which is said as
                    such rather than shown as an empty list, because "did not say" is not "cannot". */}
                <dt>Can run</dt>
                <dd data-testid="device-capabilities">
                  {device.capabilities === null || device.capabilities === undefined ? (
                    <span className="muted">not reported by this agent version</span>
                  ) : device.capabilities.length === 0 ? (
                    <span className="muted">nothing registered</span>
                  ) : (
                    device.capabilities.join(', ')
                  )}
                </dd>

                <dt>Last event</dt>
                <dd data-testid="device-last-event">{formatUtc(device.lastSeenUtc)}</dd>

                <dt>Enrolled</dt>
                <dd>{formatUtc(device.enrolledUtc)}</dd>
              </dl>

              <p className="hint">
                <strong>Last event</strong> is stamped when telemetry arrives. Only{' '}
                <strong>Last check-in</strong> says the device has spoken to the policy feed, which is
                why a machine can look busy and still be running nothing.
              </p>
            </div>
          </div>

          <div className="card" data-testid="device-active-policies">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Policies it reports running</h3>

              {policiesAbsence ? (
                <Empty>{policiesAbsence}</Empty>
              ) : (
                <div className="table-scroll">
                  <table data-testid="device-active-policy-table">
                    <thead>
                      <tr>
                        <th>Policy</th>
                        <th className="secondary">Revision</th>
                        <th className="secondary">Activated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {device.activePolicies.map((policy) => (
                        <tr key={`${policy.policyId}-${policy.revision}`}>
                          <td className="mono">
                            <Link to={`/policies/${policy.policyId}`}>{policy.policyId}</Link>
                          </td>
                          <td className="secondary">{policy.revision}</td>
                          <td className="secondary">{formatUtc(policy.activatedUtc)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <WithheldCard response={response} />

          <div className="card" data-testid="device-addresses">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Network</h3>
              {device.addresses.length === 0 ? (
                <Empty>
                  <span data-testid="device-addresses-empty">
                    No network adapter reported. Network groups match on a reported address, so this
                    device is in none of them.
                  </span>
                </Empty>
              ) : (
                <div className="table-scroll">
                  <table data-testid="device-address-table">
                    <thead>
                      <tr>
                        <th>Adapter</th>
                        <th>Address</th>
                        <th className="secondary">MAC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {device.addresses.map((address) => (
                        <tr key={`${address.adapterName}-${address.address}`}>
                          <td>{address.adapterName}</td>
                          <td className="mono">
                            {address.address}
                            {address.prefixLength === null ? '' : `/${address.prefixLength}`}
                          </td>
                          <td className="mono secondary">{address.macAddress ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="stack">
          <div className="card" data-testid="device-identity">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Identity</h3>
              <dl className="detail">
                <dt>Device id</dt>
                <dd className="mono">{device.deviceId}</dd>
                <dt>Install id</dt>
                <dd className="mono">{device.installId ?? <span className="muted">not reported</span>}</dd>
                <dt>Signed-in user</dt>
                <dd>{device.principalUser ?? <span className="muted">not reported</span>}</dd>
                {/* Still shown, as the fact it is. It left the badge in the header because it was
                    standing where an operator looks for a verdict and answering a different question. */}
                <dt>Enrollment status</dt>
                <dd data-testid="device-enrollment-status">{device.status}</dd>
                <dt>Feed tag</dt>
                <dd className="mono secondary">
                  {device.lastFeedETag ?? <span className="muted">none yet</span>}
                </dd>
              </dl>
            </div>
          </div>

          <div className="card" data-testid="device-credentials">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Credentials</h3>
              <p data-testid="device-credential-count">
                {device.liveCredentialCount === 0 ? (
                  <span className="muted">
                    No live credential. This device cannot authenticate until one is issued.
                  </span>
                ) : (
                  `${device.liveCredentialCount} live credential${device.liveCredentialCount === 1 ? '' : 's'}.`
                )}
              </p>
              {device.liveCredentialCount > 1 ? (
                <p className="hint">
                  More than one is the healthy state during rotation: a device keeps its old credential
                  until it has picked up the new one, and nothing is revoked early.
                </p>
              ) : null}
            </div>
          </div>

          <div className="card" data-testid="device-groups">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Groups</h3>
              {device.groups.length === 0 ? (
                <Empty>
                  <span data-testid="device-groups-empty">
                    In no groups. It receives only policies assigned to every device, or to it by name.
                  </span>
                </Empty>
              ) : (
                <ul className="stack">
                  {device.groups.map((group) => (
                    <li key={group.deviceGroupId}>
                      {group.name} <span className="pill">{group.kind}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <AuditChainCard deviceId={device.deviceId} />

          <RejectionCard device={device} />
        </div>
      </div>

      <Explainer summary="What this page can and cannot tell you about the machine" data-testid="device-explainer">
        <p className="muted" style={{ marginTop: 0 }}>
          Everything here is what the device last said about itself, at a check-in. Nothing on this page
          asks the machine a question: the console cannot poll a device, and it deliberately never asks
          the policy feed on a page load, because building a feed mints an envelope and would burn the
          nonce the device's next real poll needs.
        </p>
        <p className="muted">
          So an absence here is an absence of a report, not an absence on the machine. A device with no
          network adapter listed has not reported one — which still matters, because network
          groups match on a reported address and this device is therefore in none of them.
        </p>
        <p className="hint">
          The one thing this page can do to a machine is retire it, which stops it being served anything
          and returns its seat. It does not reach out and uninstall the agent.
        </p>
      </Explainer>
    </>
  )
}

/**
 * Takes this machine out of the fleet.
 *
 * The control the console kept telling operators to use and did not have. The seat card explains that
 * retiring a device returns its seat; the displaced-credential banner on this very page says to
 * "retire this device"; and `POST /devices/:id/retire` has been routed and authorized since Phase 2
 * with no caller at all (D-433).
 *
 * It asks, and it asks without a typed phrase. The ladder this console uses turns on whether a machine
 * finds out and whether anything can be put back: retiring is a status change that serves an empty feed
 * and frees a seat, and the device can be enrolled again from a kit. That is a sentence and a button,
 * not a phrase to type (D-424).
 */
function RetireButton({
  device,
  onRetired
}: {
  device: DeviceDetailResponse['device']
  onRetired: () => void
}) {
  const navigate = useNavigate()
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  if (device.status !== 'active') return null

  async function retire() {
    setAsking(false)
    setBusy(true)
    setFailure(null)
    try {
      await api.retireDevice(device.deviceId, reason || 'Retired from the console.')
      onRetired()
      navigate('/devices')
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The device could not be retired.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {failure && <ErrorBanner message={failure} />}

      <button type="button" onClick={() => setAsking(true)} disabled={busy} data-testid="retire-device">
        Retire
      </button>

      <ConfirmDialog
        open={asking}
        title={`Retire ${device.deviceName}`}
        confirmLabel="Retire it"
        danger
        busy={busy}
        // Closed before the request, so a refusal is reported on the page rather than under a modal.
        onConfirm={() => void retire()}
        onClose={() => setAsking(false)}
        data-testid="retire-device-confirm"
      >
        <p>
          This device is served an empty feed from its next check-in and stops being counted against
          this organization's seats. Everything it has already reported is kept: its events, its
          raw bodies and its audit chain are untouched.
        </p>
        <p className="hint">
          The agent is not uninstalled and nothing happens on the machine itself. If it is still
          running it keeps checking in and keeps being served nothing — enrolling it again from a
          kit is how it comes back.
        </p>

        <div className="field">
          <label htmlFor="retire-reason">Reason</label>
          <input
            id="retire-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Laptop returned"
            data-testid="retire-reason"
          />
          <div className="hint">Kept with the device, and read by whoever asks later why it left the fleet.</div>
        </div>
      </ConfirmDialog>
    </>
  )
}

/**
 * This device's tamper-evident log.
 *
 * The shell has counted these fleet-wide on every screen since the redesign - verified, broken, and
 * never submitted one - and `GET /devices/:id/audit-chain` has answered the same question per device
 * since Phase 5 with nothing calling it. So an operator could read "53 logs no longer verify" in the
 * status bar of every screen and had no way at all to find out which fifty-three (D-433).
 *
 * Three states, and the third is the one that must not be folded into the others. A device that has
 * never submitted a chain is not a device whose chain failed: it is a device no audit policy applies
 * to. Reporting it as unverified would invent an integrity finding on a machine doing exactly what it
 * was asked to.
 */
function AuditChainCard({ deviceId }: { deviceId: string }) {
  const chain = useAsync(() => api.deviceAuditChain(deviceId), [deviceId])
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const { reload } = chain

  async function accept() {
    setAsking(false)
    setBusy(true)
    setFailure(null)
    try {
      await api.acceptAuditChain(deviceId)
      reload()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The chain could not be accepted.')
    } finally {
      setBusy(false)
    }
  }

  const status = chain.value?.status ?? null

  return (
    <div className="card" data-testid="device-audit-chain">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Audit log</h3>

        <ErrorBanner message={failure ?? chain.error} />

        {!chain.value ? null : status === null ? (
          <Empty>
            <span data-testid="device-audit-chain-absent">
              This device has never submitted an audit chain. That is not a failure: no audit policy
              applies to it, so there is nothing here to verify.
            </span>
          </Empty>
        ) : (
          <>
            {status.broken ? (
              <div className="banner warning" data-testid="device-audit-chain-broken">
                This device's log no longer verifies
                {status.brokenUtc ? ` as of ${formatUtc(status.brokenUtc)}` : ''}.
                {status.brokenReason ? ` ${status.brokenReason}` : ''}
              </div>
            ) : null}

            <dl className="detail">
              <dt>Verifies</dt>
              <dd data-testid="device-audit-chain-state">
                {status.broken ? <span className="warn">no</span> : 'yes'}
              </dd>
              <dt>Entries</dt>
              <dd className="tabular">{status.entriesTotal.toLocaleString()}</dd>
              <dt>Segments</dt>
              <dd className="tabular">{status.segmentsTotal.toLocaleString()}</dd>
              <dt>Last entry</dt>
              <dd>{formatUtc(status.lastSeenUtc)}</dd>
            </dl>

            {status.broken && (
              <button type="button" onClick={() => setAsking(true)} disabled={busy} data-testid="accept-audit-chain">
                Accept the chain as it stands
              </button>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={asking}
        title="Accept this chain"
        confirmLabel="Accept it"
        busy={busy}
        onConfirm={() => void accept()}
        onClose={() => setAsking(false)}
        data-testid="accept-audit-chain-confirm"
      >
        <p>
          This records that a person looked at why the log stopped verifying and accepted it as it now
          stands. Entries from here on verify against this point; the break itself stays in the record
          and is not erased.
        </p>
        <p className="hint">
          It is deliberately not automatic. A chain that resumed on its own would be a chain that proved
          nothing, so somebody has to take responsibility for the explanation.
        </p>
      </ConfirmDialog>
    </div>
  )
}

/**
 * What this deployment cannot serve to this device, and why.
 *
 * Three states, and the middle one is the point. A determined answer with nothing withheld means the
 * device can be served everything assigned to it; an undetermined answer means the device has never
 * said what it can read, and reporting that as "0 withheld" would be the most reassuring wrong answer
 * available (D-270).
 */
function WithheldCard({ response }: { response: DeviceDetailResponse }) {
  const withheld = response.withheldPolicies

  return (
    <div className="card" data-testid="device-withheld">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Policies it cannot be served</h3>

        {/*
          Only when there is nothing to group. The grouped reason below says "this device has never
          checked in, so it has never reported which envelope format it can read" already, and the two
          sentences printed together said the same thing twice under one heading. The distinction the
          undetermined state carries - that these are unaccounted for rather than definitively
          withheld - is in that reason text, which is where it belongs (D-270, D-435).
        */}
        {!response.deliveryDetermined && withheld.length === 0 ? (
          <Empty>
            <span data-testid="device-withheld-undetermined">
              This device has never reported which envelope format it can read, so what it could be
              served cannot be worked out. Nothing is assigned to it either.
            </span>
          </Empty>
        ) : withheld.length === 0 ? (
          <Empty>
            <span data-testid="device-withheld-empty">
              Every policy assigned to this device can be served to it.
            </span>
          </Empty>
        ) : null}

        {/*
          Grouped by reason, not one row per policy, and the difference is not cosmetic.

          A device that has never checked in withholds *everything* assigned to it for the one reason
          that it has never checked in. This card rendered that as two hundred and forty-nine rows
          carrying two hundred and forty-nine copies of the same sentence, which is how the device page
          came to be twenty-two thousand pixels tall - and an operator scrolling it learns nothing on
          row two that they did not learn on row one. The reason is the actionable half, so the reason
          is the heading and the policies are the evidence under it (D-435).
        */}
        {groupByReason(withheld).map((group) => (
          <div className="withheld-group" key={group.reason ?? 'unknown'} data-testid="device-withheld-group">
            <p className="withheld-reason">
              <strong>
                {group.lines.length} {group.lines.length === 1 ? 'policy' : 'policies'}
              </strong>{' '}
              {describeWithholding(group.reason)}
            </p>

            <ul className="policy-chips" data-testid="device-withheld-policies">
              {group.lines.slice(0, WITHHELD_SHOWN).map((line) => (
                <li key={`${line.policyId}-${line.revision}`}>
                  <Link className="mono" to={`/policies/${line.policyId}`}>
                    {line.policyId}
                  </Link>
                  <span className="muted"> rev {line.revision}</span>
                </li>
              ))}
            </ul>

            {group.lines.length > WITHHELD_SHOWN && (
              <p className="hint" data-testid="device-withheld-more">
                and {group.lines.length - WITHHELD_SHOWN} more, all withheld for the same reason.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/** How many policy identifiers are listed under a reason before the count speaks for the rest. */
const WITHHELD_SHOWN = 8

/**
 * The withheld lines, gathered under the reason they share, in the order the reasons first appear.
 *
 * Order preserved rather than sorted, so the group at the top is the one the server thought of first
 * and the card does not reshuffle itself between two reads that withheld the same things.
 */
function groupByReason(
  lines: DeviceDetailResponse['withheldPolicies']
): { reason: string | null; lines: DeviceDetailResponse['withheldPolicies'] }[] {
  const groups = new Map<string, { reason: string | null; lines: DeviceDetailResponse['withheldPolicies'] }>()

  for (const line of lines) {
    const key = line.reason ?? 'unknown'
    const group = groups.get(key) ?? { reason: line.reason, lines: [] }
    group.lines.push(line)
    groups.set(key, group)
  }

  return [...groups.values()]
}

/**
 * The last envelope this device refused.
 *
 * Rendered only when there is one, and **without a time**. The check-in body carries no rejection
 * timestamp, so the server stamps the moment it heard; the device re-reports the same refusal at every
 * check-in, so that stamp advances forever and a refusal from March reads as having happened seconds
 * ago. There is no honest label for it, so the value is not on the client type at all. It is also
 * deliberately not a badge or a filter on the Devices grid: it would light once and never go out.
 */
function RejectionCard({ device }: { device: DeviceDetailResponse['device'] }) {
  if (!device.lastRejectionReason) return null

  return (
    <div className="card" data-testid="device-last-rejection">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Last refused envelope</h3>
        <p data-testid="device-rejection-reason">{device.lastRejectionReason}</p>

        <dl className="detail">
          <dt>Policy</dt>
          <dd className="mono" data-testid="device-rejection-policy">
            {device.lastRejectionPolicyId ? (
              <Link to={`/policies/${device.lastRejectionPolicyId}`}>{device.lastRejectionPolicyId}</Link>
            ) : (
              <span className="muted">the policy is not named</span>
            )}
          </dd>
        </dl>

        <p className="hint" data-testid="device-rejection-caveat">
          This is the most recent envelope this device has ever refused, not one since the last
          check-in. The device re-reports the same refusal every time it checks in, and the check-in
          carries no rejection time, so when it happened is not recorded here.
        </p>
      </div>
    </div>
  )
}
